import { useState, useEffect, useCallback } from 'react';
import { posService, restaurantService } from '../../services/dataService';

const card = {
  background: 'rgba(255,255,255,0.95)',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.06)',
};

const btn = (variant = 'primary', disabled = false) => {
  const base = {
    border: 'none', borderRadius: '10px', padding: '10px 20px',
    fontSize: '0.92rem', fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
  };
  if (disabled) return { ...base, background: '#e5e7eb', color: '#9ca3af' };
  if (variant === 'primary') return { ...base, background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', boxShadow: '0 3px 10px rgba(255,68,68,0.3)' };
  if (variant === 'outline') return { ...base, background: 'transparent', color: '#666', border: '2px solid #e5e7eb' };
  return base;
};

const fmt = (n) => `₹${(n || 0).toFixed(2)}`;

const POSApp = ({ restaurant }) => {
  const [menu, setMenu] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Cart state
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('DINE_IN');
  const [tableId, setTableId] = useState('');
  const [notes, setNotes] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const loadData = useCallback(async () => {
    if (!restaurant?.id) return;
    const [menuRes, tablesRes] = await Promise.all([
      restaurantService.getMenu(restaurant.id),
      restaurantService.getTables(restaurant.id),
    ]);
    setMenu(menuRes.data || []);
    setTables(tablesRes.data || []);
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const allItems = menu.flatMap(cat =>
    (cat.menu_items || [])
      .filter(i => !i.deleted && i.is_available !== false)
      .map(i => ({ ...i, categoryName: cat.name }))
  );
  const categories = menu.filter(cat => (cat.menu_items || []).some(i => !i.deleted && i.is_available !== false));

  const filteredItems = allItems.filter(i => {
    const matchesCategory = selectedCategory === 'all' || i.categoryName === selectedCategory;
    const matchesSearch = !search.trim() || i.name.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.menu_item_id === item.id);
      if (existing) return prev.map(c => c.menu_item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menu_item_id: item.id, name: item.name, unit_price: item.price, quantity: 1 }];
    });
  };

  const updateQty = (menuItemId, qty) => {
    if (qty <= 0) setCart(prev => prev.filter(c => c.menu_item_id !== menuItemId));
    else setCart(prev => prev.map(c => c.menu_item_id === menuItemId ? { ...c, quantity: qty } : c));
  };

  const cartTotal = cart.reduce((s, c) => s + c.unit_price * c.quantity, 0);

  const placeOrder = async () => {
    setError(''); setSuccess('');
    if (cart.length === 0) { setError('Add at least one item.'); return; }
    setPlacing(true);
    const { error: err } = await posService.createCounterOrder(restaurant.id, {
      tableId: tableId || null, orderType, items: cart, notes, restaurantName: restaurant.name,
    });
    setPlacing(false);
    if (err) { setError('Failed to place order: ' + err.message); return; }
    setCart([]); setNotes(''); setTableId('');
    setSuccess('Order placed successfully!');
    setTimeout(() => setSuccess(''), 4000);
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#888' }}>Loading menu...</div>;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '16px 12px' : '30px 20px', paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 100px)' : '30px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: '700', color: '#333' }}>Counter Order</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: '24px', alignItems: 'start' }}>

        {/* Menu grid */}
        <div style={{ ...card, padding: isMobile ? '16px' : '24px' }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Select Items</h2>
          <input
            type="text" placeholder="Search menu..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', marginBottom: '16px', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = '#FF6B00'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
          {filteredItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No items found</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(130px, 1fr))' : 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
              {filteredItems.map(item => {
                const inCart = cart.find(c => c.menu_item_id === item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => addToCart(item)}
                    style={{
                      border: inCart ? '2px solid #FF6B00' : '2px solid #e5e7eb',
                      borderRadius: '12px', padding: '14px', cursor: 'pointer',
                      background: inCart ? '#fff7ed' : 'white',
                      transition: 'all 0.15s', position: 'relative',
                    }}
                  >
                    {inCart && (
                      <div style={{
                        position: 'absolute', top: '8px', right: '8px',
                        background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
                        color: 'white', borderRadius: '50%', width: '22px', height: '22px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: '700',
                      }}>{inCart.quantity}</div>
                    )}
                    <div style={{ fontSize: '0.72rem', color: '#999', marginBottom: '4px' }}>{item.categoryName}</div>
                    <div style={{ fontWeight: '600', color: '#333', fontSize: '0.9rem', marginBottom: '4px', paddingRight: '22px' }}>{item.name}</div>
                    <div style={{ fontWeight: '700', color: '#FF4444', fontSize: '0.95rem' }}>{fmt(item.price)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Order panel — desktop: sticky card; mobile: inline card + fixed bottom bar */}
        {isMobile ? (
          <>
            {/* Mobile: order details card (no Place Order button here) */}
            <div style={{ ...card, padding: '16px' }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Current Order</h2>

              {/* Order type */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                {['DINE_IN', 'TAKEAWAY'].map(t => (
                  <button
                    key={t}
                    onClick={() => { setOrderType(t); if (t === 'TAKEAWAY') setTableId(''); }}
                    style={{ ...btn(orderType === t ? 'primary' : 'outline'), flex: 1, fontSize: '0.85rem' }}
                  >
                    {t === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
                  </button>
                ))}
              </div>

              {/* Table selector */}
              {orderType === 'DINE_IN' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '0.9rem', color: '#555', marginBottom: '6px' }}>Table</label>
                  <select
                    value={tableId}
                    onChange={e => setTableId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.95rem', outline: 'none' }}
                  >
                    <option value="">No table</option>
                    {tables.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.custom_name || `Table ${t.table_number}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Cart items */}
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa', fontSize: '0.9rem' }}>
                  Tap items from the menu to add them
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  {cart.map(item => (
                    <div key={item.menu_item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ flex: 1, fontSize: '0.88rem', color: '#333', fontWeight: '500' }}>{item.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button onClick={() => updateQty(item.menu_item_id, item.quantity - 1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: 'white', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                        <span style={{ minWidth: '18px', textAlign: 'center', fontWeight: '700', fontSize: '0.9rem' }}>{item.quantity}</span>
                        <button onClick={() => updateQty(item.menu_item_id, item.quantity + 1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: 'white', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                        <span style={{ minWidth: '56px', textAlign: 'right', fontWeight: '600', color: '#FF4444', fontSize: '0.88rem' }}>{fmt(item.unit_price * item.quantity)}</span>
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontWeight: '700', color: '#333' }}>
                    <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                  </div>
                </div>
              )}

              <textarea
                placeholder="Order notes (optional)..."
                value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', resize: 'vertical', marginBottom: '4px', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = '#FF6B00'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />

              {error && <div style={{ color: '#dc2626', fontSize: '0.88rem', marginTop: '8px' }}>{error}</div>}
              {success && <div style={{ color: '#16a34a', fontSize: '0.88rem', marginTop: '8px', fontWeight: '600' }}>{success}</div>}
            </div>

            {/* Mobile: fixed Place Order bar above Android nav controls */}
            <div style={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              background: 'white',
              borderTop: '1px solid #e5e7eb',
              padding: '12px 16px',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
              zIndex: 100,
            }}>
              <button
                onClick={placeOrder}
                disabled={placing || cart.length === 0}
                style={{ ...btn('primary', placing || cart.length === 0), width: '100%', padding: '16px', fontSize: '1rem' }}
              >
                {placing ? 'Placing...' : cart.length === 0 ? 'Place Order' : `Place Order  •  ${fmt(cartTotal)}`}
              </button>
            </div>
          </>
        ) : (
          /* Desktop: sticky order panel card */
          <div style={{ ...card, padding: '24px', position: 'sticky', top: '100px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', fontWeight: '700', color: '#333' }}>Current Order</h2>

            {/* Order type */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              {['DINE_IN', 'TAKEAWAY'].map(t => (
                <button
                  key={t}
                  onClick={() => { setOrderType(t); if (t === 'TAKEAWAY') setTableId(''); }}
                  style={{ ...btn(orderType === t ? 'primary' : 'outline'), flex: 1, fontSize: '0.85rem' }}
                >
                  {t === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
                </button>
              ))}
            </div>

            {/* Table selector */}
            {orderType === 'DINE_IN' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.9rem', color: '#555', marginBottom: '6px' }}>Table</label>
                <select
                  value={tableId}
                  onChange={e => setTableId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.95rem', outline: 'none' }}
                >
                  <option value="">No table</option>
                  {tables.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.custom_name || `Table ${t.table_number}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Cart items */}
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa', fontSize: '0.9rem' }}>
                Tap items from the menu to add them
              </div>
            ) : (
              <div style={{ marginBottom: '16px' }}>
                {cart.map(item => (
                  <div key={item.menu_item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ flex: 1, fontSize: '0.88rem', color: '#333', fontWeight: '500' }}>{item.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button onClick={() => updateQty(item.menu_item_id, item.quantity - 1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: 'white', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                      <span style={{ minWidth: '18px', textAlign: 'center', fontWeight: '700', fontSize: '0.9rem' }}>{item.quantity}</span>
                      <button onClick={() => updateQty(item.menu_item_id, item.quantity + 1)} style={{ width: '24px', height: '24px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', background: 'white', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                      <span style={{ minWidth: '56px', textAlign: 'right', fontWeight: '600', color: '#FF4444', fontSize: '0.88rem' }}>{fmt(item.unit_price * item.quantity)}</span>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontWeight: '700', color: '#333' }}>
                  <span>Subtotal</span><span>{fmt(cartTotal)}</span>
                </div>
              </div>
            )}

            <textarea
              placeholder="Order notes (optional)..."
              value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.9rem', outline: 'none', resize: 'vertical', marginBottom: '12px', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />

            {error && <div style={{ color: '#dc2626', fontSize: '0.88rem', marginBottom: '10px' }}>{error}</div>}
            {success && <div style={{ color: '#16a34a', fontSize: '0.88rem', marginBottom: '10px', fontWeight: '600' }}>{success}</div>}

            <button
              onClick={placeOrder}
              disabled={placing || cart.length === 0}
              style={{ ...btn('primary', placing || cart.length === 0), width: '100%', padding: '14px' }}
            >
              {placing ? 'Placing...' : `Place Order • ${fmt(cartTotal)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSApp;
